/* eslint-disable @typescript-eslint/camelcase, camelcase */
import { createReducer, ActionType, createAction } from 'typesafe-actions';
import { extractVimeoIdFromUrl, getVimeoPlayerUrl } from '../../urlService';
import { zip, range } from '../../../common/utils/fp';
import {
    GalleryState,
    Slide,
    APIProductImages,
    VideoRaw,
    SlideVaration,
    VideoSliderPosition,
    ProductImageTag,
} from './types';

// Helpers
export const combineRawImageArrays = (part1: Array<APIProductImages>, part2: Array<APIProductImages>) => {
    const combo = zip(part1, part2);
    return combo.map(([ogImage, otherImage]) => ({
        alt_tag: ogImage.alt_tag,
        big: otherImage.big,
        path: ogImage.path,
        seo_url: ogImage.seo_url,
        tag: ogImage.tag,
        thumb: otherImage.thumb,
        zoom: otherImage.zoom,
    }));
};
export const getIndexByVideoPosition = (position: VideoSliderPosition | undefined, sliderLength: number) => {
    switch (position) {
        case VideoSliderPosition.FIRST:
            return 0;
        case VideoSliderPosition.SECOND:
            return 1;
        case VideoSliderPosition.LAST:
            return sliderLength;
        default:
            return 1;
    }
};

export const formatThumbnailImageSrc = (cover?: string) => {
    if (!cover) {
        return '';
    }
    const urlArray = cover.split('_');
    urlArray[1] = '1080x1318.jpg';
    return urlArray.join('_');
};

export const buildSlider = ({
    rawImages,
    rawVideo,
    isVimeoVideoPlayerEnabled,
    isTwoManHandling,
}: {
    rawImages: APIProductImages[];
    rawVideo: VideoRaw;
    isVimeoVideoPlayerEnabled: boolean;
    isTwoManHandling: boolean;
}) => {
    const slides: Array<Slide> = rawImages.map(image => {
        return {
            slideType: SlideVaration.IMG,
            altTag: image.alt_tag,
            big: image.big,
            path: image.path,
            seoUrl: image.seo_url,
            tag: image.tag,
            thumb: image.thumb,
            zoom: image.zoom,
        };
    });

    if (isVimeoVideoPlayerEnabled && rawVideo.source) {
        const position = getIndexByVideoPosition(rawVideo.video_position, slides.length);
        const vimeoId = extractVimeoIdFromUrl(rawVideo.source);
        slides.splice(position, 0, {
            slideType: SlideVaration.VID,
            url: getVimeoPlayerUrl(vimeoId),
            nativeUrl: rawVideo.external_url,
            type: rawVideo.type,
            thumb: formatThumbnailImageSrc(rawVideo.cover),
            thumbWithIcon: rawVideo.cover_with_play,
            tag: 'video',
        });
    }

    if (isTwoManHandling) {
        slides.push({
            slideType: SlideVaration.TWOMH,
            tag: 'twomh',
        });
    }

    return slides;
};

export const addOrRemoveVideoToSlider = ({
    rawVideo,
    isVimeoVideoPlayerEnabled,
    slides,
}: {
    rawVideo?: VideoRaw;
    isVimeoVideoPlayerEnabled: boolean;
    slides: Slide[];
}) => {
    const currentVideoIndex = slides.findIndex(vid => vid.slideType === SlideVaration.VID);

    if (!isVimeoVideoPlayerEnabled && currentVideoIndex > -1) {
        slides.splice(currentVideoIndex, 1);
        return slides;
    }

    if (isVimeoVideoPlayerEnabled && rawVideo && rawVideo.source && currentVideoIndex < 0) {
        const position = getIndexByVideoPosition(rawVideo.video_position, slides.length);
        const vimeoId = extractVimeoIdFromUrl(rawVideo.source);
        slides.splice(position, 0, {
            slideType: SlideVaration.VID,
            url: getVimeoPlayerUrl(vimeoId),
            nativeUrl: rawVideo.external_url,
            type: rawVideo.type,
            thumb: formatThumbnailImageSrc(rawVideo.cover),
            thumbWithIcon: rawVideo.cover_with_play,
            tag: 'video',
        });
    }

    return slides;
};

// Actions
export const set = createAction('@PRODUCT_DETAILS/GALLERY/SET', action => {
    return (opt: {
        rawImagesPart1: APIProductImages[];
        rawImagesPart2: APIProductImages[];
        rawVideo: VideoRaw;
        isVimeoVideoPlayerEnabled: boolean;
        isTwoManHandling: boolean;
    }) => action(opt);
});

export const preloadFirstImage = createAction('@PRODUCT_DETAILS/GALLERY/PRELOAD_FIRST_IMG', action => {
    return (opt: { path: string; seoUrl: string; tag: ProductImageTag }) => action(opt);
});

export const setCurrentIndex = createAction('@PRODUCT_DETAILS/GALLERY/SET_CURRENT_INDEX', action => {
    return (index: number) => action({ index });
});

export const insertVideo = createAction('@PRODUCT_DETAILS/GALLERY/INSERT_VIDEO', action => {
    return (isVimeoVideoPlayerEnabled: boolean, slides: Slide[], video?: VideoRaw) =>
        action({ isVimeoVideoPlayerEnabled, video, slides });
});

// actions that can only be handled by this reducer and are not shared
export const galleryActions = [set, preloadFirstImage, setCurrentIndex];
export type GalleryActionsType =
    | ActionType<typeof set>
    | ActionType<typeof setCurrentIndex>
    | ActionType<typeof preloadFirstImage>
    | ActionType<typeof insertVideo>;

// Reducer
export const galleryDefaultState: GalleryState = {
    slides: range(0, 5).map(() => ({
        slideType: SlideVaration.BLANK,
        tag: 'blank',
    })),
    currentIndex: 0,
};

const galleryReducer = createReducer<GalleryState, GalleryActionsType>(galleryDefaultState)
    .handleAction(set, (_, action) => {
        const rawImages = combineRawImageArrays(action.payload.rawImagesPart1, action.payload.rawImagesPart2);
        return {
            ...galleryDefaultState,
            currentIndex: 0,
            slides: buildSlider({
                rawImages,
                rawVideo: action.payload.rawVideo,
                isVimeoVideoPlayerEnabled: action.payload.isVimeoVideoPlayerEnabled,
                isTwoManHandling: action.payload.isTwoManHandling,
            }),
        };
    })
    .handleAction(insertVideo, (state, action) => {
        const { isVimeoVideoPlayerEnabled, video, slides } = action.payload;
        return {
            ...state,
            slides: addOrRemoveVideoToSlider({
                rawVideo: video,
                isVimeoVideoPlayerEnabled,
                slides,
            }),
        };
    })
    .handleAction(preloadFirstImage, (state, action) => {
        const firstImage: Slide = {
            slideType: SlideVaration.IMG,
            altTag: '',
            seoUrl: action.payload.seoUrl,
            thumb: '',
            big: '',
            zoom: '',
            path: action.payload.path,
            tag: action.payload.tag,
        };

        return {
            ...state,
            slides: [firstImage],
        };
    })
    .handleAction(setCurrentIndex, (state, action) => {
        return {
            ...state,
            currentIndex: action.payload.index,
        };
    });

export default galleryReducer;
